import { confirmar } from '@/components/ui/confirm-dialog';
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSalesTrainingStore } from '../store/sales-training-store';
import {
  SalesExample,
  SalesExampleCategory,
  CATEGORY_LABELS,
  CreateExampleData,
} from '../types/sales-training.types';
import { ExampleFormDialog } from './example-form-dialog';
import { Plus, Pencil, Trash2, Loader2, MessageSquare } from 'lucide-react';

const CATEGORIES: (SalesExampleCategory | 'ALL')[] = [
  'ALL',
  'OBJECTION',
  'GREETING',
  'CLOSING',
  'PRICING',
  'GENERAL',
];

const CATEGORY_COLORS: Record<SalesExampleCategory, string> = {
  OBJECTION: 'bg-red-500/10 text-red-400 border-red-500/20',
  GREETING: 'bg-green-500/10 text-green-400 border-green-500/20',
  CLOSING: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  PRICING: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
  GENERAL: 'bg-gray-500/10 text-gray-400 border-gray-500/20',
};

export function SalesExamplesTable() {
  const { examples, isLoading, fetchExamples, createExample, updateExample, deleteExample } =
    useSalesTrainingStore();

  const [filter, setFilter] = useState<SalesExampleCategory | 'ALL'>('ALL');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExample, setEditingExample] = useState<SalesExample | null>(null);

  useEffect(() => {
    fetchExamples();
  }, [fetchExamples]);

  const filteredExamples =
    filter === 'ALL' ? examples : examples.filter((e) => e.category === filter);

  const handleCreate = useCallback(
    async (data: CreateExampleData) => {
      await createExample(data);
    },
    [createExample]
  );

  const handleUpdate = useCallback(
    async (data: CreateExampleData) => {
      if (!editingExample) return;
      await updateExample(editingExample.id, data);
      setEditingExample(null);
    },
    [editingExample, updateExample]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      if (
        await confirmar({
          title: '¿Eliminar este ejemplo?',
          variant: 'destructive',
          confirmText: 'Sí, eliminar',
        })
      ) {
        await deleteExample(id);
      }
    },
    [deleteExample]
  );

  const openCreate = () => {
    setEditingExample(null);
    setDialogOpen(true);
  };

  const openEdit = (example: SalesExample) => {
    setEditingExample(example);
    setDialogOpen(true);
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle>Ejemplos de Respuesta</CardTitle>
                <CardDescription>
                  Ejemplos que el agente usara como referencia de tono y estilo (few-shot)
                </CardDescription>
              </div>
            </div>
            <Button onClick={openCreate} size="sm">
              <Plus className="h-4 w-4 mr-1" /> Agregar
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filtro por categoria */}
          <div className="flex flex-wrap gap-2 mb-4">
            {CATEGORIES.map((cat) => (
              <Button
                key={cat}
                variant={filter === cat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(cat)}
              >
                {cat === 'ALL' ? 'Todos' : CATEGORY_LABELS[cat]}
                {cat !== 'ALL' && (
                  <span className="ml-1 text-xs opacity-70">
                    ({examples.filter((e) => e.category === cat).length})
                  </span>
                )}
              </Button>
            ))}
          </div>

          {isLoading && examples.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredExamples.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="text-sm">
                No hay ejemplos
                {filter !== 'ALL' ? ` en la categoría ${CATEGORY_LABELS[filter]}` : ''}.
              </p>
              <Button variant="outline" size="sm" className="mt-3" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-1" /> Crear primer ejemplo
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredExamples.map((example) => (
                <div
                  key={example.id}
                  className="p-4 rounded-lg border bg-card hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge className={CATEGORY_COLORS[example.category]}>
                          {CATEGORY_LABELS[example.category]}
                        </Badge>
                        {example.context && (
                          <span className="text-xs text-muted-foreground">{example.context}</span>
                        )}
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm">
                          <span className="font-medium text-muted-foreground">Cliente: </span>
                          {example.question}
                        </p>
                        <p className="text-sm">
                          <span className="font-medium text-primary">Agente: </span>
                          {example.answer}
                        </p>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Por {example.createdBy.firstName} {example.createdBy.lastName}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(example)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(example.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <ExampleFormDialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setEditingExample(null);
        }}
        onSubmit={editingExample ? handleUpdate : handleCreate}
        example={editingExample}
      />
    </>
  );
}

export default SalesExamplesTable;
